import React, { useEffect, useState } from 'react';
import { Box, Grid, Typography, Paper, Divider, Chip, Card, CardContent } from '@mui/material';
import { AccountCircle, Assessment, CalendarToday, Star, TrendingUp } from '@mui/icons-material';
import Logo from "../../assets/logo.jpg";

const PreviewUserResult = ({ assessment, recommendation, interpretation, finalScore, client, currentUser }) => {
    const [, setRefresh] = useState(false);

    useEffect(() => {
        setRefresh(true);
    }, [assessment]);

    const formatUserName = () => {
        const firstName = currentUser.fname || "";
        const lastName = currentUser.lname || "";

        const capitalizeEachWord = (str) => {
            return str.replace(/\b\w/g, (char) => char.toUpperCase());
        };

        return `${capitalizeEachWord(firstName)} ${capitalizeEachWord(lastName)}`.trim();
    };

    const formatDate = (dateString) => {
        // Handle DD/MM/YYYY format
        if (dateString.includes('/')) {
            const [day, month, year] = dateString.split('/');
            const date = new Date(year, month - 1, day); // month is 0-indexed
            return date.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
            });
        }
        // Fallback for other formats
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const ScoreCard = ({ title, value, icon }) => (
        <Paper
            elevation={2}
            sx={{
                p: 2,
                textAlign: 'center',
                background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
                border: '1px solid #e0e0e0',
                borderRadius: 2
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
                {icon}
                <Typography variant="body2" sx={{ ml: 1, fontWeight: 600, color: '#666' }}>
                    {title}
                </Typography>
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#0a0068' }}>
                {value}
            </Typography>
        </Paper>
    );

    const ContentSection = ({ title, children, icon }) => (
        <Paper
            elevation={1}
            sx={{
                p: 3,
                mb: 3,
                borderRadius: 2,
                border: '1px solid #e8e8e8',
                '&:hover': {
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                }
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                {icon}
                <Typography
                    variant="h6"
                    sx={{
                        ml: 1,
                        fontWeight: 700,
                        color: '#0a0068',
                        fontSize: '16px'
                    }}
                >
                    {title}
                </Typography>
            </Box>
            <Divider sx={{ mb: 2, borderColor: '#e0e0e0' }} />
            {children}
        </Paper>
    );

    return (
        <Box sx={{
            maxWidth: '1200px',
            margin: '0 auto',
            p: 4,
            backgroundColor: '#fafafa',
            minHeight: '100vh'
        }}>
            {/* Header Section */}
            {/* <Paper
                elevation={3}
                sx={{
                    p: 3,
                    mb: 4,
                    borderRadius: 3,
                    // background: 'linear-gradient(135deg, #0a0068 0%, #cc4049 100%)',
                    color: 'white'
                }}
            > */}
            <Card
                elevation={20}
                sx={{
                    mb: 6,
                    borderRadius: 6,
                    background: 'linear-gradient(135deg, #0a0068 0%, #cc4049 100%)',
                    color: 'white',
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        right: 0,
                        width: '200px',
                        height: '200px',
                        background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)',
                        transform: 'translate(50%, -50%)'
                    }
                }}
            >
                <CardContent sx={{ p: 4 }}>
                    <Grid container alignItems="center" spacing={2}>
                        <Grid item>
                            <Box sx={{
                                width: 70,
                                height: 70,
                                borderRadius: 2,
                                overflow: 'hidden',
                                boxShadow: '0 8px 20px rgba(0,0,0,0.2)',
                                border: '2px solid rgba(255,255,255,0.3)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: 'white',
                                p: 1
                            }}>
                                <img
                                    src={Logo}
                                    alt="Logo"
                                    style={{
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'contain'
                                    }}
                                />
                            </Box>
                        </Grid>
                        <Grid item xs>
                            <Box sx={{ textAlign: 'center', px: 2 }}>
                                <Typography
                                    variant="h3"
                                    sx={{
                                        fontWeight: 800,
                                        mb: 1,
                                        textShadow: '0 4px 8px rgba(0,0,0,0.3)',
                                        letterSpacing: '0.5px',
                                        fontSize: { xs: '1.8rem', md: '2.5rem' }
                                    }}
                                >
                                    {assessment.assignment_name}
                                </Typography>
                                <Chip
                                    icon={<Star />}
                                    label="Assessment Report"
                                    variant="outlined"
                                    sx={{
                                        color: 'white',
                                        borderColor: 'rgba(255,255,255,0.5)',
                                        fontWeight: 600,
                                        fontSize: '0.85rem',
                                        '& .MuiChip-icon': { color: 'white' }
                                    }}
                                />
                            </Box>
                        </Grid>
                        <Grid item>
                            <Box sx={{ textAlign: 'right', minWidth: 180 }}>
                                <Box sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'flex-end',
                                    mb: 1.5,
                                    gap: 1
                                }}>
                                    <AccountCircle sx={{ fontSize: 24 }} />
                                    <Typography
                                        variant="h6"
                                        sx={{
                                            fontWeight: 700,
                                            fontSize: '1.1rem'
                                        }}
                                    >
                                        {formatUserName()}
                                    </Typography>
                                </Box>
                                <Chip
                                    label={client}
                                    sx={{
                                        background: 'rgba(255,255,255,0.25)',
                                        color: 'white',
                                        fontWeight: 600,
                                        backdropFilter: 'blur(10px)',
                                        fontSize: '0.85rem',
                                        border: '1px solid rgba(255,255,255,0.2)'
                                    }}
                                />
                            </Box>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>
            {/* </Paper> */}

            {/* Results Section */}
            <div>
                {/* Score Overview */}
                <Paper
                    elevation={3}
                    sx={{
                        p: { xs: 2, sm: 3, md: 4 },
                        mb: 4,
                        borderRadius: 3,
                        background: '#0a0068',
                        position: 'relative',
                        overflow: 'hidden',
                        '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            background: 'rgba(255, 255, 255, 0.1)',
                            backdropFilter: 'blur(10px)',
                        }
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            gap: { xs: 2, sm: 3, md: 4 },
                            flexWrap: 'nowrap',
                            overflowX: 'auto',
                            position: 'relative',
                            zIndex: 1
                        }}
                    >
                        {/* Final Score */}
                        <Box
                            sx={{
                                flex: '1 1 0',
                                minWidth: { xs: '100px', sm: '120px' },
                                textAlign: 'center',
                                transition: 'transform 0.2s',
                                '&:hover': {
                                    transform: 'scale(1.05)'
                                }
                            }}
                        >
                            <Typography
                                variant="overline"
                                sx={{
                                    color: 'white',
                                    letterSpacing: 1.5,
                                    fontSize: { xs: '0.65rem', sm: '0.75rem' },
                                    fontWeight: 700,
                                    display: 'block',
                                    mb: 1
                                }}
                            >
                                Final Score
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: 0.5 }}>
                                <Typography
                                    variant="h3"
                                    sx={{
                                        fontWeight: 800,
                                        color: 'white',
                                        fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
                                        textShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                    }}
                                >
                                    {finalScore}
                                </Typography>
                                {/* <Typography
                                        variant="h6"
                                        sx={{
                                            color: 'rgba(255, 255, 255, 0.8)',
                                            fontWeight: 300,
                                            fontSize: { xs: '1rem', sm: '1.25rem' }
                                        }}
                                    >
                                        %
                                    </Typography> */}
                            </Box>
                        </Box>

                        {/* Divider */}
                        <Divider
                            orientation="vertical"
                            flexItem
                            sx={{
                                borderColor: 'rgba(255, 255, 255, 0.2)',
                                display: { xs: 'none', sm: 'block' }
                            }}
                        />

                        {/* Competency Level */}
                        <Box
                            sx={{
                                flex: '1 1 0',
                                minWidth: { xs: '120px', sm: '150px' },
                                textAlign: 'center'
                            }}
                        >
                            <Typography
                                variant="overline"
                                sx={{
                                    color: 'white',
                                    letterSpacing: 1.5,
                                    fontSize: { xs: '0.65rem', sm: '0.75rem' },
                                    fontWeight: 700,
                                    display: 'block',
                                    mb: 1
                                }}
                            >
                                Competency Level
                            </Typography>
                            <Chip
                                label={interpretation.name}
                                sx={{
                                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                                    color: 'white',
                                    fontSize: { xs: '0.9rem', sm: '1.1rem' },
                                    fontWeight: 600,
                                    py: { xs: 1.5, sm: 2.5 },
                                    px: { xs: 2, sm: 3 },
                                    height: 'auto',
                                    '& .MuiChip-label': {
                                        px: { xs: 1, sm: 2 }
                                    }
                                }}
                            />
                        </Box>

                        {/* Divider */}
                        <Divider
                            orientation="vertical"
                            flexItem
                            sx={{
                                borderColor: 'rgba(255, 255, 255, 0.2)',
                                display: { xs: 'none', sm: 'block' }
                            }}
                        />

                        {/* Assessment Date */}
                        <Box
                            sx={{
                                flex: '1 1 0',
                                minWidth: { xs: '130px', sm: '160px' },
                                textAlign: 'center'
                            }}
                        >
                            <Typography
                                variant="overline"
                                sx={{
                                    color: 'white',
                                    letterSpacing: 1.5,
                                    fontSize: { xs: '0.65rem', sm: '0.75rem' },
                                    fontWeight: 700,
                                    display: 'block',
                                    mb: 1
                                }}
                            >
                                Assessment Date
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                                <CalendarToday
                                    sx={{
                                        color: 'rgba(255, 255, 255, 0.8)',
                                        fontSize: { xs: '1rem', sm: '1.2rem' }
                                    }}
                                />
                                <Typography
                                    variant="body1"
                                    sx={{
                                        fontWeight: 500,
                                        color: 'white',
                                        fontSize: { xs: '0.875rem', sm: '1rem' }
                                    }}
                                >
                                    {formatDate(assessment.created_at)}
                                </Typography>
                            </Box>
                        </Box>
                    </Box>
                </Paper>

                {/* Interpretations Section */}
                <ContentSection
                    title="Interpretations"
                    icon={<Assessment sx={{ color: '#0a0068' }} />}
                >
                    <Box
                        sx={{
                            p: 3,
                            backgroundColor: '#f8f9fa',
                            borderRadius: 2,
                            border: '1px solid #e9ecef'
                        }}
                    >
                        <div
                            style={{
                                fontSize: "14px",
                                lineHeight: "1.7",
                                color: '#333'
                            }}
                            dangerouslySetInnerHTML={{ __html: interpretation.description }}
                        />
                    </Box>
                </ContentSection>

                {/* Recommendations Section */}
                <ContentSection
                    title="Recommendations"
                    icon={<TrendingUp sx={{ color: '#0a0068' }} />}
                >
                    <Box
                        sx={{
                            p: 3,
                            backgroundColor: '#f8f9fa',
                            borderRadius: 2,
                            border: '1px solid #e9ecef'
                        }}
                    >
                        <div
                            style={{
                                fontSize: "14px",
                                lineHeight: "1.7",
                                color: '#333'
                            }}
                            dangerouslySetInnerHTML={{ __html: recommendation.description }}
                        />
                    </Box>
                </ContentSection>
            </div>
        </Box>
    );
};

export default PreviewUserResult;